import { useEffect, useState } from "react"
import { displaySource } from "../demo"
import { Panel, severityClasses } from "../Panel"
import type { AiForecastItem } from "../dashboard-types"
import { useChat, useRegion } from "../store"

const CATEGORIES = ["All", "Supply Chain", "Market", "Weather", "Port", "Cyber", "Infra"] as const
const REGIONS = ["All Regions", "MENA", "Asia", "Europe", "Americas", "Africa"] as const

/** Map display label back to the raw category enum value. */
function categoryKey(label: string): AiForecastItem["category"] | null {
  const map: Record<string, AiForecastItem["category"]> = {
    "Supply Chain": "supply_chain",
    Market: "market",
    Weather: "weather",
    Port: "port",
    Cyber: "cyber",
    Infra: "infra",
  }
  return map[label] ?? null
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60_000)
  if (m < 1) return "just now"
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

export function AiForecasts({ items, demo }: { items: AiForecastItem[]; demo?: boolean }) {
  const [activeCat, setActiveCat] = useState<string>("All")
  const [activeRegion, setActiveRegion] = useState<string>("All Regions")
  const openRegion = useRegion((s) => s.openRegion)

  // LLM set_panel_filter support (panel_id "ai-forecasts"): `category` accepts
  // the raw enum ("supply_chain") or display label; `region` matches the
  // region pill labels case-insensitively.
  const filters = useChat((s) => s.panelFilters["ai-forecasts"])
  useEffect(() => {
    const cat = filters?.["category"]
    if (cat) {
      const label = CATEGORIES.find(
        (c) => c.toLowerCase() === cat.toLowerCase() || categoryKey(c) === cat.toLowerCase(),
      )
      if (label) setActiveCat(label)
    }
    const region = filters?.["region"]
    if (region) {
      const match = REGIONS.find((r) => r.toLowerCase() === region.toLowerCase())
      if (match) setActiveRegion(match)
    }
  }, [filters])

  const filtered = items.filter((it) => {
    const catKey = categoryKey(activeCat)
    if (catKey && it.category !== catKey) return false
    if (activeRegion !== "All Regions" && it.region !== activeRegion) return false
    return true
  })

  return (
    <Panel title="AI Forecasts" live demo={demo} count={items.length}>
      <div className="px-3 pt-2 pb-1 flex flex-col gap-1.5">
        {/* Category filters */}
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={activeCat === c}
              onClick={() => setActiveCat(c)}
              className="btn-pill text-[9px]"
            >
              {c}
            </button>
          ))}
        </div>
        {/* Region filters */}
        <div className="flex flex-wrap gap-1">
          {REGIONS.map((r) => (
            <button
              key={r}
              type="button"
              aria-pressed={activeRegion === r}
              onClick={() => setActiveRegion(r)}
              className="btn-pill text-[9px]"
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      <div>
        {filtered.map((it) => {
          const sv = severityClasses(it.severity)
          return (
            <div key={it.id} className="px-3 py-2 border-b border-[color:var(--border)] last:border-b-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${sv.bg} ${sv.text}`}>
                  {it.category.replace("_", " ")}
                </span>
                <button
                  type="button"
                  onClick={() => openRegion(it.region)}
                  className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200 transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)]"
                >
                  {it.region}
                </button>
              </div>
              <div className="mt-1 text-sm text-[color:var(--text)] leading-snug">{it.title}</div>
              <div className="mt-1 text-[9px] text-[color:var(--text-dim)] uppercase tracking-wider">
                {/* Fabricated model attributions (gpt-4o / claude-sonnet) are
                    scrubbed — these are simulated forecasts, not LLM output. */}
                {it.demo || demo ? "Simulated forecast" : displaySource(it.source)} · conf{" "}
                {Math.round(it.confidence * 100)}% · {relTime(it.generated_at)}
              </div>
            </div>
          )
        })}
        {filtered.length === 0 && (
          <div className="px-3 py-6 text-center text-[11px] text-[color:var(--text-dim)] uppercase tracking-wider">
            no forecasts match filters
          </div>
        )}
      </div>
    </Panel>
  )
}
